import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { dispatch, acars, ofpApi } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { fmtDate } from "@/lib/format";
import { withFlightFooter, stripFlightFooter } from "@/lib/flight-footer";
import { MessageSquare, Send, Loader2, X, CheckCircle2, Circle, ShieldAlert, Trash2, Plane } from "lucide-react";
import { toast } from "sonner";

/**
 * Dispatcher – pilot ↔ admin messaging.
 *
 *   Pilots see only their own threads.
 *   Admins see every open/closed thread across the airline and can reply,
 *   close, or delete them.
 */
export default function Dispatch() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [selectedId, setSelectedId] = useState(null);
  const [composing, setComposing] = useState(false);

  const { data: threads = [], isLoading } = useQuery({
    queryKey: ["dispatch-threads"],
    queryFn: dispatch.threads,
    refetchInterval: 15000,
  });

  const { data: availability } = useQuery({
    queryKey: ["dispatch-availability"],
    queryFn: dispatch.getAvailability,
    enabled: !!user?.is_admin,
  });
  const { data: onDuty = [] } = useQuery({
    queryKey: ["dispatch-onduty"],
    queryFn: dispatch.onDuty,
    refetchInterval: 30000,
  });

  const toggleAvail = useMutation({
    mutationFn: (v) => dispatch.setAvailability(v),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["dispatch-availability"] });
      qc.invalidateQueries({ queryKey: ["dispatch-onduty"] });
      toast.success(res.available ? "You're on duty" : "You're off duty");
    },
  });

  const selected = threads.find((t) => t.id === selectedId) || null;

  return (
    <div className="p-8 max-w-[1600px]" data-testid="dispatch-page">
      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="overline mb-1">Ops Comms</div>
          <h1 className="text-4xl font-bold tracking-tight">Dispatcher</h1>
          <div className="acbar" />
          <p className="text-sm text-muted-foreground mt-4 max-w-2xl">
            {user?.is_admin
              ? "All open pilot requests appear here. Assign gates, answer diversion or fuel queries, and close threads once resolved."
              : "Chat directly with the on-duty dispatcher. Use this if a gate isn't published or you need help with your flight plan."}
          </p>
        </div>
        <button
          onClick={() => { setComposing(true); setSelectedId(null); }}
          data-testid="dispatch-new-btn"
          className="bg-primary text-primary-foreground text-sm font-bold uppercase tracking-widest rounded-md px-4 py-2.5 hover:brightness-110 flex items-center gap-2"
        >
          <MessageSquare className="w-4 h-4" /> New request
        </button>
      </div>

      {/* Availability + on-duty banner */}
      {user?.is_admin && (
        <div className="card-flat px-5 py-3 mb-4 flex items-center gap-4 flex-wrap" data-testid="dispatch-availability-bar">
          <button
            onClick={() => toggleAvail.mutate(!availability?.available)}
            disabled={toggleAvail.isPending}
            data-testid="dispatch-toggle-availability"
            className={`text-xs font-bold uppercase tracking-widest rounded-md px-4 py-2 flex items-center gap-2 transition-colors ${
              availability?.available
                ? "bg-primary text-primary-foreground hover:brightness-110"
                : "bg-secondary text-muted-foreground hover:text-primary border border-border"
            }`}
          >
            <span className={`w-2 h-2 rounded-full ${availability?.available ? "bg-foreground" : "bg-muted-foreground"}`} />
            {availability?.available ? "You're ON DUTY" : "You're off duty — click to go on duty"}
          </button>
          <div className="text-xs text-muted-foreground flex-1">
            {onDuty.length > 0 ? (
              <>
                <span className="overline mr-2">On duty now:</span>
                <span className="font-mono text-foreground">
                  {onDuty.map((a) => `${a.pid} (${a.name})`).join(" · ")}
                </span>
              </>
            ) : (
              <span className="text-primary">⚠ No dispatchers on duty. Pings will still fire but nobody has claimed the shift.</span>
            )}
          </div>
        </div>
      )}
      {!user?.is_admin && (
        <div className="card-flat px-5 py-3 mb-4 text-xs" data-testid="dispatch-onduty-bar">
          {onDuty.length > 0 ? (
            <>
              <span className="overline mr-2">Dispatchers on duty:</span>
              <span className="font-mono text-foreground">
                {onDuty.map((a) => a.name).join(" · ")}
              </span>
            </>
          ) : (
            <span className="text-muted-foreground">No dispatcher on duty right now — your message will still be recorded and pinged to the channel.</span>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[360px_1fr] gap-4">
        {/* Thread list */}
        <aside className="card-flat overflow-hidden" data-testid="dispatch-list">
          <div className="px-4 py-3 border-b border-border">
            <div className="overline">Threads · {threads.length}</div>
          </div>
          {isLoading ? (
            <div className="p-6 text-sm text-muted-foreground flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading…
            </div>
          ) : threads.length === 0 ? (
            <div className="p-6 text-sm text-muted-foreground text-center">
              No requests yet.
            </div>
          ) : (
            <ul className="divide-y divide-border max-h-[calc(100vh-14rem)] overflow-y-auto">
              {threads.map((t) => (
                <li key={t.id}>
                  <button
                    onClick={() => { setSelectedId(t.id); setComposing(false); }}
                    data-testid={`dispatch-thread-${t.id}`}
                    className={`w-full text-left px-4 py-3 hover:bg-secondary transition-colors ${
                      selectedId === t.id ? "bg-secondary" : ""
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="text-sm font-bold truncate">{t.subject}</div>
                      {((user?.is_admin && t.unread_admin) || (!user?.is_admin && t.unread_pilot)) && (
                        <span className="w-2 h-2 rounded-full bg-primary shrink-0" title="Unread" />
                      )}
                    </div>
                    <div className="text-xs font-mono text-muted-foreground mt-1 flex items-center gap-2">
                      {t.flight && <span>{t.flight}</span>}
                      {t.orig && t.dest && <span>{t.orig}→{t.dest}</span>}
                      <span className={`ml-auto ${t.status === "OPEN" ? "text-primary" : ""}`}>
                        {t.status}
                      </span>
                    </div>
                    {user?.is_admin && (
                      <div className="text-[11px] text-muted-foreground mt-1 font-mono">
                        {t.pilot_pid} · {t.pilot_name}
                      </div>
                    )}
                    <div className="text-[11px] text-muted-foreground mt-0.5">
                      {fmtDate(new Date(t.updated_at).getTime() / 1000)}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </aside>

        {/* Detail pane */}
        <section className="min-h-[400px]" data-testid="dispatch-detail">
          {composing ? (
            <NewThreadForm
              onCreated={(t) => {
                qc.invalidateQueries({ queryKey: ["dispatch-threads"] });
                qc.invalidateQueries({ queryKey: ["dispatch-unread"] });
                setComposing(false);
                setSelectedId(t.id);
              }}
              onCancel={() => setComposing(false)}
            />
          ) : selected ? (
            <ThreadDetail
              threadId={selected.id}
              isAdmin={!!user?.is_admin}
              onDone={() => qc.invalidateQueries({ queryKey: ["dispatch-threads"] })}
            />
          ) : (
            <div className="card-flat p-10 text-center text-muted-foreground">
              <MessageSquare className="w-8 h-8 mx-auto mb-3 opacity-50" />
              Select a thread on the left or start a new request.
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

// ============================ Thread Detail ==================================
function ThreadDetail({ threadId, isAdmin, onDone }) {
  const qc = useQueryClient();
  const [reply, setReply] = useState("");
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const { data: t, isLoading } = useQuery({
    queryKey: ["dispatch-thread", threadId],
    queryFn: () => dispatch.thread(threadId),
    refetchInterval: 10000,
  });

  const send = useMutation({
    mutationFn: () => dispatch.reply(threadId, reply.trim()),
    onSuccess: () => {
      setReply("");
      qc.invalidateQueries({ queryKey: ["dispatch-thread", threadId] });
      qc.invalidateQueries({ queryKey: ["dispatch-threads"] });
      qc.invalidateQueries({ queryKey: ["dispatch-unread"] });
      onDone?.();
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Reply failed"),
  });

  const toggleStatus = useMutation({
    mutationFn: () => dispatch.setStatus(threadId, t.status === "OPEN" ? "CLOSED" : "OPEN"),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["dispatch-thread", threadId] });
      qc.invalidateQueries({ queryKey: ["dispatch-threads"] });
      toast.success(t.status === "OPEN" ? "Thread closed" : "Reopened");
    },
  });

  const del = useMutation({
    mutationFn: () => dispatch.remove(threadId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["dispatch-threads"] });
      setConfirmingDelete(false);
      toast.success("Thread deleted");
      onDone?.();
    },
    onError: (e) => {
      setConfirmingDelete(false);
      toast.error(e.response?.data?.detail || e.message || "Delete failed");
    },
  });

  if (isLoading || !t) return (
    <div className="card-flat p-8 text-sm text-muted-foreground flex items-center gap-2">
      <Loader2 className="w-4 h-4 animate-spin" /> Loading thread…
    </div>
  );

  const closed = t.status === "CLOSED";

  return (
    <div className="card-flat flex flex-col max-h-[calc(100vh-8rem)]" data-testid={`dispatch-thread-detail-${t.id}`}>
      <header className="px-6 py-4 border-b border-border flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-1">
            {closed ? (
              <CheckCircle2 className="w-4 h-4 text-muted-foreground" />
            ) : (
              <Circle className="w-4 h-4 text-primary fill-primary" />
            )}
            <span className={`overline ${closed ? "" : "text-primary"}`}>{t.status}</span>
          </div>
          <h2 className="text-lg font-bold" data-testid="dispatch-detail-subject">{t.subject}</h2>
          <div className="text-xs font-mono text-muted-foreground mt-1 flex items-center gap-2 flex-wrap">
            {t.flight && <span className="flex items-center gap-1"><Plane className="w-3 h-3" /> {t.flight}</span>}
            {t.orig && t.dest && <span>{t.orig}→{t.dest}</span>}
            <span>opened {fmtDate(new Date(t.created_at).getTime() / 1000)}</span>
          </div>
          {isAdmin && (
            <div className="text-xs text-muted-foreground mt-1">
              From <span className="font-mono text-foreground">{t.pilot_pid}</span> · {t.pilot_name}
            </div>
          )}
        </div>
        {isAdmin && (
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => toggleStatus.mutate()}
              disabled={toggleStatus.isPending}
              data-testid="dispatch-toggle-status"
              className="text-xs bg-secondary rounded-md px-3 py-1.5 hover:bg-primary hover:text-primary-foreground transition-colors uppercase tracking-widest font-mono"
            >
              {closed ? "Reopen" : "Close"}
            </button>
            {confirmingDelete ? (
              <div className="flex items-center gap-1" data-testid="dispatch-delete-confirm">
                <span className="text-[11px] text-destructive font-mono uppercase tracking-widest">Delete?</span>
                <button
                  onClick={() => setConfirmingDelete(false)}
                  data-testid="dispatch-delete-abort"
                  className="text-xs border border-border rounded-md px-2.5 py-1.5 text-muted-foreground hover:border-primary hover:text-primary"
                >
                  Keep
                </button>
                <button
                  onClick={() => del.mutate()}
                  disabled={del.isPending}
                  data-testid="dispatch-delete-confirm-btn"
                  className="text-xs bg-destructive text-destructive-foreground rounded-md px-3 py-1.5 hover:brightness-110 disabled:opacity-60 flex items-center gap-1 font-mono uppercase tracking-widest"
                >
                  {del.isPending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
                  Confirm
                </button>
              </div>
            ) : (
              <button
                onClick={() => setConfirmingDelete(true)}
                data-testid="dispatch-delete"
                className="text-xs border border-border rounded-md px-3 py-1.5 text-muted-foreground hover:border-primary hover:text-primary transition-colors flex items-center gap-1"
              >
                <Trash2 className="w-3 h-3" /> Delete
              </button>
            )}
          </div>
        )}
      </header>

      <div className="flex-1 overflow-y-auto p-6 space-y-3" data-testid="dispatch-messages">
        {(t.messages || []).map((m, i) => (
          <MessageBubble key={i} msg={m} mine={m.author_pid === (isAdmin ? m.author_pid : m.author_pid) && m.is_admin === isAdmin} />
        ))}
      </div>

      {closed ? (
        <div className="px-6 py-4 border-t border-border text-xs text-muted-foreground italic text-center">
          This thread is closed. {isAdmin ? "Reopen it to send a reply." : "A dispatcher has resolved this — start a new request if needed."}
        </div>
      ) : (
        <form
          onSubmit={(e) => { e.preventDefault(); if (reply.trim()) send.mutate(); }}
          className="px-6 py-4 border-t border-border flex gap-2"
        >
          <input
            value={reply}
            onChange={(e) => setReply(e.target.value)}
            placeholder={isAdmin ? "Reply to pilot…" : "Type a message to dispatch…"}
            data-testid="dispatch-reply-input"
            className="flex-1 bg-background border border-border rounded-md px-3 py-2 text-sm text-foreground focus:outline-none focus:border-primary"
          />
          <button
            type="submit"
            disabled={!reply.trim() || send.isPending}
            data-testid="dispatch-reply-send"
            className="bg-primary text-primary-foreground text-xs font-bold uppercase tracking-widest rounded-md px-4 hover:brightness-110 disabled:opacity-60 flex items-center gap-2"
          >
            {send.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
            Send
          </button>
        </form>
      )}
    </div>
  );
}

function MessageBubble({ msg }) {
  const isAdmin = msg.is_admin;
  return (
    <div className={`flex ${isAdmin ? "justify-start" : "justify-end"}`}>
      <div className={`max-w-[75%] rounded-lg px-4 py-2.5 ${
        isAdmin
          ? "bg-secondary border border-border"
          : "bg-primary text-primary-foreground"
      }`}>
        <div className={`text-[11px] font-mono uppercase tracking-widest mb-1 flex items-center gap-1 ${
          isAdmin ? "text-primary" : "opacity-90"
        }`}>
          {isAdmin && <ShieldAlert className="w-3 h-3" />}
          {msg.author_name || msg.author_pid} {isAdmin ? "· Dispatch" : ""}
        </div>
        <div className="text-sm whitespace-pre-wrap break-words">{msg.body}</div>
        <div className={`text-[10px] mt-1 font-mono ${isAdmin ? "text-muted-foreground" : "opacity-70"}`}>
          {new Date(msg.ts).toLocaleString()}
        </div>
      </div>
    </div>
  );
}

// ============================ Templates =====================================
const REQUEST_TEMPLATES = [
  {
    key: "GATE_DEP_REQ",
    label: "Departure Gate",
    subject: (ctx) => `Departure gate · ${ctx.flt || "flight"} at ${ctx.orig || "orig"}`,
    body:    (ctx) => `Requesting departure gate for ${ctx.flt || "[flight]"} at ${ctx.orig || "[orig]"}.`,
  },
  {
    key: "GATE_ARR_REQ",
    label: "Arrival Gate",
    subject: (ctx) => `Arrival gate · ${ctx.flt || "flight"} at ${ctx.dest || "dest"}`,
    body:    (ctx) => `Requesting arrival gate for ${ctx.flt || "[flight]"} at ${ctx.dest || "[dest]"}.`,
  },
  {
    key: "DIV_REQ",
    label: "Diversion Request",
    subject: (ctx) => `Diversion request · ${ctx.flt || "flight"} ${ctx.orig || "orig"}→${ctx.dest || "dest"}`,
    body:    (ctx) => `Requesting diversion.\n\nReason: [enter reason here]\nProposed alternate: [ICAO]\nFuel remaining: [kg]\nSouls on board: [pax + crew]`,
  },
  {
    key: "WX_REQ",
    label: "WX Request",
    subject: (ctx) => `Weather brief · ${ctx.dest || ctx.flt || "flight"}`,
    body:    (ctx) => `Requesting weather brief.\n\nAirport(s): ${[ctx.orig, ctx.dest].filter(Boolean).join(", ") || "[ICAO]"}\nType: METAR / TAF / SIGMET (delete as required)\nComments: `,
  },
  {
    key: "FUEL_REQ",
    label: "Fuel Query",
    subject: (ctx) => `Fuel query · ${ctx.flt || "flight"}`,
    body:    (ctx) => `Question about block fuel for ${ctx.flt || "[flight]"} ${ctx.orig || ""}→${ctx.dest || ""}.\n\nDetail: `,
  },
  {
    key: "PIREP_REQ",
    label: "PIREP Help",
    subject: (ctx) => `PIREP help · ${ctx.flt || "flight"}`,
    body:    (ctx) => `Need help filing / correcting the PIREP for ${ctx.flt || "[flight]"} ${ctx.orig || ""}→${ctx.dest || ""}.\n\nDetail: `,
  },
  {
    key: "GEN_REQ",
    label: "General",
    subject: () => "",
    body:    () => "",
  },
];

// ============================ New Thread Form ================================
function NewThreadForm({ onCreated, onCancel }) {
  // Prefill from the pilot's active flight (if any).
  const { data: active } = useQuery({
    queryKey: ["acars-active"],
    queryFn: acars.active,
    refetchInterval: 15_000,
  });
  const { data: ofp } = useQuery({
    queryKey: ["ofp-latest"],
    queryFn: ofpApi.latest,
    enabled: !!active,
    retry: false,
    staleTime: 60_000,
  });

  const activeFlt = active ? `${active.opr || active.operator || "AC"}${active.flt}` : "";

  const [subject, setSubject] = useState("");
  const [userBody, setUserBody] = useState("");   // pilot-typed portion only
  const [flight, setFlight] = useState(activeFlt);
  const [orig, setOrig]     = useState(active?.orig || "");
  const [dest, setDest]     = useState(active?.dest || "");
  const [reqType, setReqType] = useState("GEN_REQ");

  // Preview shows userBody + auto-appended flight-info footer.
  const previewBody = withFlightFooter(userBody, active, ofp);

  // Once the active flight loads, backfill blank fields.
  useEffect(() => {
    if (!active) return;
    setFlight((v) => v || activeFlt);
    setOrig((v) => v || active.orig || "");
    setDest((v) => v || active.dest || "");
  }, [active?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const applyTemplate = (tpl) => {
    const ctx = { flt: flight || activeFlt, orig, dest };
    setSubject(tpl.subject(ctx));
    setUserBody(tpl.body(ctx));         // footer appended automatically in preview
    setReqType(tpl.key);
  };

  const create = useMutation({
    mutationFn: () => dispatch.create({
      subject: subject.trim(),
      body: previewBody.trim(),         // send the composed body with footer
      flight: flight.trim() || null,
      orig: orig.trim() || null,
      dest: dest.trim() || null,
      request_type: reqType,
    }),
    onSuccess: (t) => { toast.success("Message sent to dispatch"); onCreated(t); },
    onError: (e) => toast.error(e.response?.data?.detail || "Failed"),
  });

  return (
    <div className="card-flat p-6" data-testid="dispatch-new-form">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-bold">New dispatch request</h3>
        <button onClick={onCancel} className="text-muted-foreground hover:text-foreground">
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Quick templates */}
      <div className="mb-4" data-testid="dispatch-templates">
        <div className="overline mb-2">Quick templates</div>
        <div className="flex flex-wrap gap-2">
          {REQUEST_TEMPLATES.map((tpl) => (
            <button
              key={tpl.key}
              type="button"
              onClick={() => applyTemplate(tpl)}
              data-testid={`dispatch-template-${tpl.key}`}
              className={`text-[11px] rounded-md px-3 py-1.5 font-mono uppercase tracking-widest transition-colors ${
                reqType === tpl.key
                  ? "bg-primary text-primary-foreground"
                  : "bg-secondary text-foreground hover:bg-primary hover:text-primary-foreground"
              }`}
            >
              {tpl.label}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-4">
        <label className="block">
          <span className="overline block mb-1.5">Subject *</span>
          <input
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="e.g. Gate assignment for AC123"
            data-testid="dispatch-new-subject"
            className="w-full bg-background border border-border rounded-md px-3 py-2 text-sm text-foreground focus:outline-none focus:border-primary"
          />
        </label>
        <div className="grid grid-cols-3 gap-3">
          <label className="block">
            <span className="overline block mb-1.5">Flight</span>
            <input
              value={flight}
              onChange={(e) => setFlight(e.target.value.toUpperCase())}
              placeholder="AC123"
              data-testid="dispatch-new-flight"
              className="w-full bg-background border border-border rounded-md px-3 py-2 text-sm font-mono text-foreground focus:outline-none focus:border-primary"
            />
          </label>
          <label className="block">
            <span className="overline block mb-1.5">Orig</span>
            <input
              value={orig}
              onChange={(e) => setOrig(e.target.value.toUpperCase())}
              placeholder="CYYZ"
              data-testid="dispatch-new-orig"
              className="w-full bg-background border border-border rounded-md px-3 py-2 text-sm font-mono text-foreground focus:outline-none focus:border-primary"
            />
          </label>
          <label className="block">
            <span className="overline block mb-1.5">Dest</span>
            <input
              value={dest}
              onChange={(e) => setDest(e.target.value.toUpperCase())}
              placeholder="CYVR"
              data-testid="dispatch-new-dest"
              className="w-full bg-background border border-border rounded-md px-3 py-2 text-sm font-mono text-foreground focus:outline-none focus:border-primary"
            />
          </label>
        </div>
        <label className="block">
          <span className="overline block mb-1.5">
            Message *
            {active && <span className="normal-case tracking-normal text-muted-foreground ml-2">· flight info auto-appended from live tracking</span>}
          </span>
          <textarea
            value={userBody}
            onChange={(e) => setUserBody(stripFlightFooter(e.target.value))}
            rows={5}
            placeholder="Tap a template above or write your own message here."
            data-testid="dispatch-new-body"
            className="w-full bg-background border border-border rounded-md px-3 py-2 text-sm text-foreground focus:outline-none focus:border-primary font-mono"
          />
        </label>

        {/* Live flight-info preview (only when there is an active flight) */}
        {active && (
          <div className="border border-primary/40 rounded-md p-3 bg-primary/5" data-testid="dispatch-new-footer-preview">
            <div className="overline mb-2">Auto-appended from live tracking</div>
            <pre className="text-xs font-mono whitespace-pre-wrap break-words text-foreground">
{withFlightFooter("", active, ofp).replace(/^\s+/, "")}
            </pre>
          </div>
        )}
        <div className="flex justify-end gap-2">
          <button
            onClick={onCancel}
            className="text-xs border border-border rounded-md px-4 py-2 uppercase tracking-widest font-mono text-muted-foreground hover:border-primary hover:text-primary"
          >
            Cancel
          </button>
          <button
            onClick={() => create.mutate()}
            disabled={!subject.trim() || !userBody.trim() || create.isPending}
            data-testid="dispatch-new-submit"
            className="bg-primary text-primary-foreground text-xs font-bold uppercase tracking-widest rounded-md px-6 py-2 hover:brightness-110 disabled:opacity-60 flex items-center gap-2"
          >
            {create.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
            Send to dispatch
          </button>
        </div>
      </div>
    </div>
  );
}
